import { faList } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Box, Typography } from "@mui/material";
import type { ReactNode } from "react";

import { collectionDataTypeLabel } from "./columns/collection-management-columns";
import CoAppDataGridDateTimeCell from "@/components/global/components/datagrid/CoAppDataGridDateTimeCell";
import { CoAppDataGridHeaderTitle } from "@/components/global/styled/global.styled";

import type { Collection } from "./collectionsApi";

const metadataFields: { key: keyof Collection | "dataTypeLabel"; label: string }[] = [
	{ key: "name", label: "Name" },
	{ key: "rulesUsingCount", label: "# of Rules Using this Collection" },
	{ key: "dataTypeLabel", label: "Data Type" },
	{ key: "platformName", label: "Platform" },
	{ key: "createdAt", label: "Created At" },
	{ key: "updatedAt", label: "Updated At" },
];

function MetadataValue({ collection, field }: { collection: Collection; field: typeof metadataFields[number] }) {
	if (field.key === "dataTypeLabel") {
		return (
			<Typography variant="body2" color="text.primary">
				{collectionDataTypeLabel(collection.dataTypeId)}
			</Typography>
		);
	}
	if (field.key === "createdAt" || field.key === "updatedAt") {
		return <CoAppDataGridDateTimeCell value={collection[field.key]} />;
	}
	if (field.key === "rulesUsingCount") {
		return (
			<Typography variant="body2" color="text.primary">
				{collection.rulesUsingCount}
			</Typography>
		);
	}
	return (
		<Typography variant="body2" color="text.primary" sx={{ wordBreak: "break-word" }}>
			{String(collection[field.key as keyof Collection] ?? "")}
		</Typography>
	);
}

interface CollectionDetailSummaryProps {
	collection: Collection;
	trailing?: ReactNode;
}

export default function CollectionDetailSummary({ collection, trailing }: CollectionDetailSummaryProps) {
	const pageTitle = `${collection.name} Collection Management`;

	return (
		<Box
			sx={{
				width: "100%",
				flexShrink: 0,
				borderBottom: "1px solid rgba(13, 31, 48, 0.08)",
				background: "#FFFFFF",
			}}
		>
			<Box
				sx={{
					display: "flex",
					alignItems: "flex-start",
					justifyContent: "space-between",
					gap: 2,
					px: 3,
					py: 2,
					background: "#FFFFFF",
				}}
			>
				<Box sx={{ display: "flex", alignItems: "center", gap: 1.25, minWidth: 0 }}>
					<Box
						sx={{
							display: "inline-flex",
							"& svg": { width: 26, height: 26 },
							"& svg path": { fill: "url(#navIconGradient)" },
						}}
					>
						<FontAwesomeIcon icon={faList} />
					</Box>
					<CoAppDataGridHeaderTitle variant="h1" color="text.primary" title={pageTitle} sx={{ m: 0 }}>
						{pageTitle}
					</CoAppDataGridHeaderTitle>
				</Box>
				{trailing ? <Box sx={{ flexShrink: 0 }}>{trailing}</Box> : null}
			</Box>
			<Box
				sx={{
					display: "grid",
					gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))",
					gap: 2,
					rowGap: 1.5,
					px: 3,
					py: 2,
					background: "rgba(13, 31, 48, 0.04)",
					borderTop: "1px solid rgba(13, 31, 48, 0.06)",
				}}
			>
				{metadataFields.map((field) => (
					<Box key={field.key}>
						<Typography
							variant="caption"
							sx={{ display: "block", fontWeight: 600, color: "rgba(13, 31, 48, 0.45)", mb: 0.25 }}
						>
							{field.label}
						</Typography>
						<MetadataValue collection={collection} field={field} />
					</Box>
				))}
			</Box>
		</Box>
	);
}
